import { useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PageHeading } from '@/components/PageHeading';
import type { ApiError } from '@/lib/api';
import { useMoney } from '@/lib/money';

/**
 * A screen's filters: open on a wide screen, folded behind a button on a phone.
 *
 * The fields are the same either way. What changes is that on a phone they stack
 * full-width — five of them is over 350px of chrome, more than a phone's whole
 * viewport — and a reader who has already set them is scrolling past them on every
 * visit to reach what they set them for. Folded, the report starts at the top of
 * the screen and the filters are one tap away; the button says how many are not at
 * their default so nothing is hidden silently.
 */
function FilterBar({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className="filter-bar">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 py-0.5 text-sm font-semibold text-ink sm:hidden"
      >
        <span className="flex items-center gap-2">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            className="size-4 text-ink-muted"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 4.5h18m-14 5h10m-7 5h4"
            />
          </svg>
          {t('reports.filters')}
        </span>
        <span aria-hidden="true" className="text-xs text-ink-muted">
          {open ? '▲' : '▼'}
        </span>
      </button>

      <div
        className={clsx(
          'min-w-0 sm:overflow-visible',
          open ? 'mt-3 block sm:mt-0' : 'hidden sm:block',
        )}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * The chrome every report screen shares: a heading, the date controls, and the
 * loading, error, and empty states.
 *
 * Extracted after the second report rather than the first. The three primary
 * statements differ only in their body, and repeating the error and empty
 * handling per screen is how one of them ends up silently rendering a blank page
 * on failure.
 */
export function ReportFrame<TData>({
  title,
  subtitle,
  controls,
  banner,
  actions,
  query,
  isEmpty,
  children,
}: {
  readonly title: string;
  /** Controls beside the print button, such as "New ledger". */
  readonly actions?: React.ReactNode;
  /** A line under the heading, for a period or a scope the title cannot carry. */
  readonly subtitle?: string;
  readonly controls: React.ReactNode;
  /**
   * A notice above the filters, for something that happened before the screen opened.
   *
   * Above them and outside the query's own states on purpose: a screen somebody was
   * redirected to has to say why while the report under it is still loading, and a
   * notice inside the body would not appear until the figures did.
   */
  readonly banner?: React.ReactNode;
  readonly query: UseQueryResult<TData, ApiError>;
  readonly isEmpty?: (data: TData) => boolean;
  readonly children: (data: TData) => React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();

  // Subscribed to, not read. The report body below is a function this calls, and the
  // `money` it uses inside reads the setting at call time — so this subscription is
  // what makes a change to the precision repaint the figures instead of leaving the
  // old ones until something else happens to re-render the screen.
  useMoney();

  return (
    // `page-lean` where the screen has no filter strip: the list below is sized to
    // what the screen has left, and that is 70px more without one.
    <section className={clsx('page', !controls && 'page-lean')}>
      {/*
        The title is not rendered here at all: `PageHeading` puts it in the
        application bar, which already spans the screen and had nothing on it but a
        wordmark and three switches.

        It used to open the page — first on the same row as the filters, then on a
        row of its own above them. Both spent a band of the content area repeating
        what the highlighted menu entry says, and on a laptop that band is three
        rows of the list underneath it. The print control goes up with it: it
        belongs to the screen as a whole, not to the figures.
      */}
      <PageHeading
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {actions}
            <button
              type="button"
              onClick={() => window.print()}
              className="btn-icon shrink-0"
              title={t('reports.print')}
              aria-label={t('reports.print')}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-[18px]"
                aria-hidden="true"
              >
                <path d="M6 9V3h12v6" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
                <path d="M6 14h12v7H6z" />
              </svg>
            </button>
          </>
        }
      />

      {/*
        Rendered only where a screen actually has filters — an empty bar is a line of
        furniture saying nothing. Folded away below `sm`, where the same five fields
        stack full-width and fill a phone twice over before a single row of the
        report is reached.
      */}
      {banner}

      {controls && <FilterBar>{controls}</FilterBar>}

      {/*
        A refetch of a report that is already on screen shows a hairline rather than
        replacing the figures with a spinner. Blanking a statement somebody is
        reading in order to say "loading" costs them their place.
      */}
      {query.isFetching && !query.isPending && <div className="progress-sweep" />}

      {query.isError && (
        <div role="alert" className="alert-error">
          <p className="font-semibold">{query.error.code}</p>
          <p className="mt-0.5 opacity-90">{query.error.detail}</p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="mt-2 rounded-md px-2 py-1 text-sm font-semibold underline underline-offset-2 transition hover:bg-red-500/10"
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {query.isPending && <ReportSkeleton />}

      {query.data !== undefined &&
        (isEmpty?.(query.data) === true ? (
          <EmptyState message={t('reports.noData')} />
        ) : (
          children(query.data)
        ))}
    </section>
  );
}

/**
 * What a report looks like before it arrives.
 *
 * Shaped like the table that is coming rather than a generic spinner, so the screen
 * settles into place instead of jumping when the figures land.
 */
export function ReportSkeleton({
  rows = 6,
}: {
  readonly rows?: number;
}): React.JSX.Element {
  return (
    <div className="card overflow-hidden" aria-hidden="true">
      <div className="flex gap-4 border-b border-line bg-surface-3 px-4 py-3">
        {Array.from({ length: 4 }, (_, index) => (
          <span key={index} className="skeleton h-3 flex-1 rounded" />
        ))}
      </div>
      <div className="divide-y divide-line">
        {Array.from({ length: rows }, (_, row) => (
          <div key={row} className="flex gap-4 px-4 py-3.5">
            {Array.from({ length: 4 }, (_, column) => (
              <span
                key={column}
                className="skeleton h-3 flex-1 rounded"
                // Varied widths, because four identical bars per row reads as a
                // loading graphic and staggered ones read as text that has not
                // arrived yet.
                style={{
                  maxWidth: `${60 + ((row * 7 + column * 23) % 40)}%`,
                  animationDelay: `${(row * 4 + column) * 45}ms`,
                }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Says plainly that a query succeeded and matched nothing. The hint is not shown. */
export function EmptyState({
  message,
  hint: _hint,
}: {
  readonly message: string;
  readonly hint?: string;
}): React.JSX.Element {
  return (
    <div className="card animate-pop flex flex-col items-center gap-2.5 px-6 py-14 text-center">
      <div className="grid size-12 place-items-center rounded-2xl bg-surface-3 text-ink-subtle ring-1 ring-line">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-5"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.6-3.6" />
        </svg>
      </div>
      <p className="text-sm font-semibold text-ink">{message}</p>
    </div>
  );
}

/** A from/to date range control that only applies on submit. */
export function DateRangeControls({
  from,
  to,
  onFromChange,
  onToChange,
  onApply,
  busy,
}: {
  readonly from: string;
  readonly to: string;
  readonly onFromChange: (value: string) => void;
  readonly onToChange: (value: string) => void;
  readonly onApply: () => void;
  readonly busy: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <form
      className="toolbar"
      onSubmit={(event) => {
        event.preventDefault();
        onApply();
      }}
    >
      <div className="field">
        <label htmlFor="from" className="field-label">
          {t('reports.from')}
        </label>
        <input
          id="from"
          type="date"
          className="field-input"
          value={from}
          onChange={(e) => onFromChange(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="to" className="field-label">
          {t('reports.to')}
        </label>
        <input
          id="to"
          type="date"
          className="field-input"
          value={to}
          onChange={(e) => onToChange(e.target.value)}
        />
      </div>
      <button type="submit" disabled={busy} className="btn-primary">
        {busy && <Spinner />}
        {busy ? t('reports.running') : t('reports.run')}
      </button>
    </form>
  );
}

/** A single as-at date control. */
export function AsAtControls({
  asAt,
  onChange,
  onApply,
  busy,
}: {
  readonly asAt: string;
  readonly onChange: (value: string) => void;
  readonly onApply: () => void;
  readonly busy: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <form
      className="toolbar"
      onSubmit={(event) => {
        event.preventDefault();
        onApply();
      }}
    >
      <div className="field">
        <label htmlFor="asAt" className="field-label">
          {t('reports.asAt')}
        </label>
        <input
          id="asAt"
          type="date"
          className="field-input"
          value={asAt}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      <button type="submit" disabled={busy} className="btn-primary">
        {busy && <Spinner />}
        {busy ? t('reports.running') : t('reports.run')}
      </button>
    </form>
  );
}

/** The ring that turns inside a button while its action is in flight. */
export function Spinner({
  className,
}: {
  readonly className?: string;
}): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className={clsx('size-4 shrink-0 animate-spin', className)}
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        opacity="0.25"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * The balance indicator.
 *
 * Coloured and prominent because a statement that does not balance means the
 * books are broken. Rendering the figures without saying so leaves a reader to
 * compare two totals and hope they notice.
 */
export function BalanceBadge({
  isBalanced,
  currency,
}: {
  readonly isBalanced: boolean;
  readonly currency: string;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <p
      className={clsx(
        'inline-flex animate-pop items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium',
        isBalanced
          ? 'border-emerald-200/70 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/12 dark:text-emerald-200'
          : 'border-red-200/70 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-500/12 dark:text-red-200',
      )}
    >
      {/*
        A dot as well as the colour. Roughly one man in twelve cannot tell this
        green from this red, and "the books balance" is not a fact to encode in hue
        alone — so it pulses when it is wrong, and the words say which it is.
      */}
      <span
        aria-hidden="true"
        className={clsx(
          'size-2 rounded-full',
          isBalanced ? 'bg-emerald-500' : 'animate-breathe bg-red-500',
        )}
      />
      {isBalanced ? `${t('reports.balanced')} · ${currency}` : t('reports.notBalanced')}
    </p>
  );
}

/*
  Re-exported from `lib/money` rather than defined here.

  Every report in the application already imports these two from this module, and
  the number of decimal places is now the branch's to decide. Keeping the names
  where they are means each of those screens follows the setting without being
  touched — and `ReportFrame` itself subscribes to it below, so a report body, which
  it calls as a function, is re-rendered when the setting changes rather than
  showing the old precision until the next navigation.
*/
export { money, moneyAlways } from '@/lib/money';
